import { withOwnerPoints, hasOwnerAward, privateTransitionWins } from '../utils/projectOwnerState';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { accountGeneration } from '../lib/accountStorage';
import { onSignOut } from '../lib/signOutReset';
import type { Project } from '../types';
import type { ProjectReducer, ProjectReducerContext, ProjectReduction } from './projectReducers';
import { useCollaborationStore } from './collaborationStore';
import { useProjectStore, reducerContext, persistPrivateProject, finalizePrivateProject } from './projectStore';
import { useActivityLogStore } from './activityLogStore';
import { queueProjectActivity, readFullProjectHistory, clearProjectActivity, fetchProjectActivity, useProjectActivityRemote } from './projectActivityRemote';
import { convertLegacyMilestones } from '../utils/projectMilestones';
import { sharedProjectData, ownerMirror, sameContent, intentReducer, findClash, deferCompletion, resolvePending, copyHistory } from './sharedProjectModel';

export const SAVE_ATTEMPTS = 3;
export interface SharedRow { id: string; owner_id: string; revision: number; data: Project }
type Apply = (project: Project, ctx: ProjectReducerContext) => ProjectReduction;
interface Intent { before: Project; desired: Project; apply: Apply; ctx: ProjectReducerContext }
export interface SaveState { busy?: boolean; error?: string; clash?: { field: string; theirs: string; name: string }; intent?: Intent }
export const useSharedProjectWrites = create<{ byProject: Record<string, SaveState> }>(() => ({ byProject: {} }));
const stateFor = (id: string, value: SaveState) => useSharedProjectWrites.setState(state => ({ byProject: { ...state.byProject, [id]: value } }));
const errorMessage = (error: unknown) => error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error);
const lifecycle = new Set<string>();
export function setSharedCopyWaiting(id: string, waiting: boolean): void {
  if (useSharedProjectWrites.getState().byProject[id]?.intent) return;
  stateFor(id, waiting ? { error: 'The shared project is unavailable. Waiting for your private copy to sync.' } : {});
}
export const lifecycleInProgress = (id: string) => lifecycle.has(id);

export async function readSharedRow(id: string): Promise<SharedRow> {
  const { data, error } = await supabase.from('projects').select('id, owner_id, data, revision, title, status, progress').eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data?.data || !Array.isArray(data.data.tasks)) throw new Error('This shared project is no longer available.');
  return { ...data, revision: Number(data.revision), data: sharedProjectData(convertLegacyMilestones({ ...data.data, id })) };
}

/**
 * ⚠️⚠️ A SAVE DOES NOT OUTLIVE ITS ACCOUNT. A sign-out does not reload the page, and every
 * request runs as whoever is signed in when it is MADE: a save still on its way for the
 * account that left wrote the next account's requests (its owner writes, its deletes),
 * and its answers landed in the next account's stores — the row in their collaboration
 * store, the owner's mirror in their PRIVATE projects (their blob sync then saved it as
 * theirs), the XP award in their activity log, the error and the held edit on their
 * screen. Each save notes `accountGeneration()` (lib/accountStorage) before its first
 * await and does nothing more once it has moved.
 */
class AccountChanged extends Error {
  constructor() { super('Signed out before the project was saved.'); }
}

/**
 * The former bridge, now the ONLY shared-content UPDATE. Reducers re-run on each fresh row.
 * `at`: the account generation the save began under (default: now).
 */
export async function writeWithRevision(id: string, apply: (row: SharedRow) => ProjectReduction | Promise<ProjectReduction>, options: { attempts?: number; initial?: SharedRow; at?: number } = {}): Promise<{ row: SharedRow; reduction: ProjectReduction; written: boolean }> {
  const at = options.at ?? accountGeneration();
  for (let attempt = 0; attempt < (options.attempts ?? SAVE_ATTEMPTS); attempt++) {
    const row = attempt === 0 && options.initial ? options.initial : await readSharedRow(id);
    const reduction = await apply(row);
    if (sameContent(row.data, reduction.project)) return { row, reduction: { ...reduction, mints: [], events: [] }, written: false };
    // The update below would run as the account signed in now — not the one this save is for.
    if (at !== accountGeneration()) throw new AccountChanged();
    const data = sharedProjectData(reduction.project);
    const { data: written, error } = await supabase.from('projects').update({ data, title: data.title, status: data.status, progress: data.progress ?? 0, revision: row.revision + 1 }).eq('id', id).eq('revision', row.revision).select('revision');
    if (error && error.code !== 'PR409') throw error;
    if (!error && written?.length) return { row: { ...row, data, revision: row.revision + 1 }, reduction, written: true };
  }
  throw new Error('The project kept changing after three attempts.');
}
export function applySharedRow(row: SharedRow, me: string): void {
  row = { ...row, data: sharedProjectData(row.data) };
  const collab = useCollaborationStore.getState();
  const known = collab.sharedMeta[row.id];
  const localMirror = useProjectStore.getState().projects.find(project => project.id === row.id);
  if (row.owner_id === me && !localMirror?.privateSince?.pending && privateTransitionWins(localMirror, row.revision)) return;
  if ((known?.revision ?? -1) > row.revision) return;
  if (row.owner_id === me && (localMirror?.sharedMirror?.revision ?? -1) > row.revision) {
    row = { ...row, revision: localMirror!.sharedMirror!.revision, data: sharedProjectData(localMirror!) };
  }
  useCollaborationStore.setState({ sharedProjects: [...collab.sharedProjects.filter(p => p.id !== row.id), row.data], sharedMeta: { ...collab.sharedMeta, [row.id]: { ...known, ownerId: row.owner_id, isOwner: me === row.owner_id, myRole: me === row.owner_id ? 'owner' : known?.myRole ?? 'viewer', revision: row.revision } } });
  if (row.owner_id === me && !lifecycle.has(row.id)) {
    useProjectStore.setState(state => {
      const local = state.projects.find(p => p.id === row.id);
      if (local?.sharedMirror && local.sharedMirror.revision > row.revision) return {};
      return { projects: [...state.projects.filter(p => p.id !== row.id), ownerMirror(row.data, local, row.revision)] };
    });
  }
}
/** Accept only a successful owner-client reduction, never point records read from a row. */
function rememberOwnerResult(row: SharedRow, reduction: ProjectReduction): void {
  useProjectStore.setState(state => {
    const local = state.projects.find(project => project.id === row.id);
    const base = local ?? ownerMirror(row.data, undefined, row.revision);
    const trusted = withOwnerPoints(base, local, reduction.project);
    return { projects: [...state.projects.filter(project => project.id !== row.id), trusted] };
  });
}
function ownerInput(row: SharedRow): Project {
  return ownerMirror(row.data, useProjectStore.getState().projects.find(project => project.id === row.id));
}
function journal(reduction: ProjectReduction): void {
  for (const mint of reduction.mints) void useActivityLogStore.getState().addActivity({ activityType: 'project', ...mint });
}
function actorName(projectId: string, actorId?: string): string {
  const member = useCollaborationStore.getState().membersByProject[projectId]?.find(member => member.userId === actorId);
  return member?.displayName ?? member?.handle ?? (actorId ? useProjectActivityRemote.getState().actorNames[actorId] : undefined) ?? 'A former member';
}
async function pendingNames(project: Project): Promise<(id?: string) => string> {
  const ids = [...new Set([project, ...project.tasks, ...project.tasks.flatMap(task => task.subTasks ?? [])].flatMap(item => item.xpPending && item.completedBy ? [item.completedBy] : []))];
  const names: Record<string, string> = {};
  if (ids.length) {
    const { data, error } = await supabase.from('user_profiles').select('id, name, handle').in('id', ids);
    if (error) throw error;
    for (const profile of data ?? []) if (profile.name || profile.handle) names[profile.id] = profile.name || profile.handle;
  }
  return id => names[id ?? ''] ?? actorName(project.id, id);
}
export function isSharedProject(id: string): boolean {
  const local = useProjectStore.getState().projects.find(project => project.id === id);
  if (local?.privateSince?.pending) return true;
  if (privateTransitionWins(local, useCollaborationStore.getState?.()?.sharedMeta?.[id]?.revision ?? -1)) return false;
  return !!useProjectStore.getState().projects.find(p => p.id === id)?.sharedMirror || !!useCollaborationStore.getState?.()?.sharedMeta?.[id] || !!useCollaborationStore.getState?.()?.sharedProjects?.some(p => p.id === id);
}
export function editableSnapshot(id: string): Project | undefined {
  const collab = useCollaborationStore.getState?.();
  const local = useProjectStore.getState().projects.find(p => p.id === id);
  if (!collab || !local?.privateSince?.pending && privateTransitionWins(local, collab.sharedMeta?.[id]?.revision ?? -1)) return local;
  const shared = collab.sharedProjects?.find(p => p.id === id);
  return shared ? collab.sharedMeta?.[id]?.isOwner ? ownerMirror(shared, local, collab.sharedMeta[id].revision) : shared : local;
}
class ClashError extends Error {
  constructor(readonly row: SharedRow, readonly clash: { field: string; theirs: string }) { super('Someone changed the same field.'); }
}
async function execute(id: string, intent: Intent): Promise<void> {
  const at = accountGeneration();
  let confirmed: SharedRow | undefined;
  stateFor(id, { busy: true, intent });
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) throw new Error('Sign in to save this shared project.');
    if (!useCollaborationStore.getState().sharedMeta[id]) await useCollaborationStore.getState().fetchShared();
    if (!useCollaborationStore.getState().canEdit(id)) throw new Error('You no longer have permission to edit this project.');
    if (lifecycle.has(id)) throw new Error('Sharing is changing. Try again when it finishes.');
    const result = await writeWithRevision(id, async row => {
      confirmed = row;
      const clash = findClash(intent.before, intent.desired, row.data);
      if (clash) throw new ClashError(row, clash);
      const owner = row.owner_id === user.id;
      const ctx = { ...intent.ctx, mint: owner ? 'mint' as const : 'defer' as const };
      const pending = owner ? resolvePending(ownerInput(row), ctx, await pendingNames(row.data)) : { project: sharedProjectData(row.data), mints: [] };
      const reduced = intent.apply(pending.project, ctx);
      const project = owner ? withOwnerPoints(reduced.project, pending.project, reduced.project) : deferCompletion(sharedProjectData(row.data), sharedProjectData(reduced.project), user.id);
      const mints = reduced.mints.filter(mint => !hasOwnerAward(pending.project, mint.metadata.taskId, mint.metadata.subTaskId));
      return { ...reduced, project, mints: [...pending.mints, ...mints] };
    }, { at });
    if (at !== accountGeneration()) return; // saved for the account that left: nothing of it is the next one's
    if (result.written && result.row.owner_id === user.id) rememberOwnerResult(result.row, result.reduction);
    applySharedRow(result.row, user.id);
    if (result.written) {
      const retained = result.row.owner_id === user.id ? useProjectStore.getState().projects.find(p => p.id === id)?.activity ?? [] : [];
      const events = result.reduction.events.map(event => ({ ...event, id: crypto.randomUUID() }));
      queueProjectActivity(id, user.id, [...retained.filter(event => event.actorId === undefined || event.actorId === user.id), ...events]);
      journal(result.reduction);
    }
    useCollaborationStore.setState({ error: null });
    stateFor(id, {});
  } catch (error) {
    if (at !== accountGeneration()) return; // not the next account's error, nor its held edit
    if (confirmed) {
      const { sharedMeta, sharedProjects } = useCollaborationStore.getState();
      useCollaborationStore.setState({ sharedProjects: sharedProjects.map(project => project.id === id ? confirmed!.data : project) });
      if (sharedMeta[id]?.isOwner) applySharedRow(confirmed, confirmed.owner_id);
    } else {
      useCollaborationStore.setState(state => ({ sharedProjects: state.sharedProjects.map(project => project.id === id ? sharedProjectData(intent.before) : project) }));
    }
    const message = errorMessage(error);
    useCollaborationStore.setState({ error: message });
    if (error instanceof ClashError) {
      await fetchProjectActivity(id);
      if (at !== accountGeneration()) return;
      const actor = useProjectActivityRemote.getState().rows[id]?.[0]?.actorId;
      stateFor(id, { intent, clash: { ...error.clash, name: actor ? actorName(id, actor) : 'Another member' } });
    } else stateFor(id, { intent, error: message });
  }
}
const draftBaselines = new Map<string, Project>();
/** Draft/drag baseline is scoped synchronously; private actions do not consume it. */
export function withSharedEditBaseline<T>(project: Project, action: () => T): T {
  const previous = draftBaselines.get(project.id); draftBaselines.set(project.id, project);
  try { return action(); } finally { if (previous) draftBaselines.set(project.id, previous); else draftBaselines.delete(project.id); }
}
const queued = new Map<string, { intent: Intent; done: () => void }[]>();
async function drain(id: string): Promise<void> {
  if (useSharedProjectWrites.getState().byProject[id]?.intent) return;
  const next = queued.get(id)?.shift();
  if (!next) return;
  await execute(id, next.intent);
  next.done();
  if (!useSharedProjectWrites.getState().byProject[id]?.intent) await drain(id);
}
export function routeSharedEdit<A>(id: string, reducer: ProjectReducer<A>, args: A, ctx: ProjectReducerContext): Promise<void> {
  const before = draftBaselines.get(id) ?? editableSnapshot(id);
  if (!before) return Promise.resolve();
  // Held edits are resolved explicitly; never overwrite an unsaved intent.
  const apply = intentReducer(before, reducer, args, ctx);
  const desired = apply(before, { ...ctx, mint: 'defer' }).project;
  const held = useSharedProjectWrites.getState().byProject[id];
  if (!held?.intent || held.busy) useCollaborationStore.setState(state => ({ sharedProjects: state.sharedProjects.map(project => project.id === id ? sharedProjectData(desired) : project) }));
  return new Promise<void>(done => {
    const list = queued.get(id) ?? [];
    list.push({ intent: { before, desired, apply, ctx }, done }); queued.set(id, list);
    void drain(id);
  });
}
export async function retrySharedEdit(id: string, keepMine = false): Promise<void> {
  const intent = useSharedProjectWrites.getState().byProject[id]?.intent;
  if (!intent) return;
  if (keepMine) {
    const at = accountGeneration();
    try {
      const row = await readSharedRow(id);
      if (at !== accountGeneration()) return; // the edit was the account's that left
      intent.before = row.data; intent.desired = intent.apply(row.data, { ...intent.ctx, mint: 'defer' }).project;
    } catch (error) { if (at === accountGeneration()) stateFor(id, { intent, error: errorMessage(error) }); return; }
  }
  await execute(id, intent);
  if (!useSharedProjectWrites.getState().byProject[id]?.intent) await drain(id);
}
export function discardSharedEdit(id: string): void { stateFor(id, {}); for (const item of queued.get(id) ?? []) item.done(); queued.delete(id); useCollaborationStore.setState({ error: null }); }

/** First-fetch bridge for old owner blobs. A refused push adopts, never retries. */
export async function reconcileSharedRow(row: SharedRow, me: string): Promise<void> {
  const at = accountGeneration();
  if (lifecycle.has(row.id) || useSharedProjectWrites.getState().byProject[row.id]?.busy) return;
  const local = useProjectStore.getState().projects.find(p => p.id === row.id);
  if (row.owner_id === me && !local?.privateSince?.pending && privateTransitionWins(local, row.revision)) return;
  if (row.owner_id === me && local && !local.sharedMirror && !local.privateSince && !sameContent(local, row.data)) {
    try {
      row = (await writeWithRevision(row.id, () => ({ project: sharedProjectData(local), mints: [], events: [], warnings: [] }), { attempts: 1, initial: row })).row;
    } catch (error) {
      if (error instanceof Error && error.message === 'The project kept changing after three attempts.') row = await readSharedRow(row.id);
      else throw error;
    }
  }
  if (row.owner_id === me) {
    const ctx = reducerContext(useProjectStore.getState().userLevel);
    const result = await writeWithRevision(row.id, async current => resolvePending(ownerInput(current), ctx, await pendingNames(current.data)), { initial: row, at });
    // ⚠️⚠️ `me` has signed out: their mirror and award would land in the next account's projects and log.
    if (at !== accountGeneration()) return;
    row = result.row;
    if (result.written) { rememberOwnerResult(result.row, result.reduction); journal(result.reduction); }
  }
  applySharedRow(row, me);
}

export async function stopSharing(id: string, deleting = false): Promise<boolean> {
  if (lifecycle.has(id) || useSharedProjectWrites.getState().byProject[id]?.intent) return false;
  const at = accountGeneration();
  lifecycle.add(id);
  stateFor(id, { busy: true });
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) throw new Error('Sign in first.');
    for (let attempt = 0; attempt < SAVE_ATTEMPTS; attempt++) {
      let row = await readSharedRow(id);
      if (row.owner_id !== user.id) throw new Error('Only the owner can stop sharing or delete this project.');
      if (!deleting) {
        const history = await readFullProjectHistory(id, user.id);
        // Resolve under CAS, after every required read succeeded. If it loses the
        // race, restart the history read too; never pair fresh content with old history.
        try {
          const pending = await writeWithRevision(id, async fresh => resolvePending(ownerInput(fresh), reducerContext(useProjectStore.getState().userLevel), await pendingNames(fresh.data)), { initial: row, attempts: 1, at });
          // The private copy would be the NEXT account's — saved to their projects as theirs.
          if (at !== accountGeneration()) return false;
          row = pending.row;
          if (pending.written) { rememberOwnerResult(pending.row, pending.reduction); journal(pending.reduction); }
        } catch (error) {
          if (error instanceof Error && error.message === 'The project kept changing after three attempts.') continue;
          throw error;
        }
        const local = useProjectStore.getState().projects.find(p => p.id === id);
        const copy = { ...ownerMirror(row.data, local), activity: copyHistory(local?.activity ?? [], history.events), privateSince: { revision: row.revision, at: new Date().toISOString(), pending: true as const }, updatedAt: new Date().toISOString() };
        await persistPrivateProject(copy);
      }
      if (at !== accountGeneration()) return false; // the delete would run as the next account
      const { data, error } = await supabase.from('projects').delete().eq('id', id).eq('revision', row.revision).select('id');
      if (error) throw error;
      if (!data?.length) continue;
      // Deleted, as this account. What follows only REMOVES the project, so nothing lands even
      // when the account changed while the delete was answered.
      if (!deleting) finalizePrivateProject(id);
      clearProjectActivity(id);
      useCollaborationStore.setState(state => ({ sharedProjects: state.sharedProjects.filter(project => project.id !== id), sharedMeta: Object.fromEntries(Object.entries(state.sharedMeta).filter(([key]) => key !== id)), error: null }));
      stateFor(id, {});
      return true;
    }
    throw new Error('The project kept changing. Nothing was deleted. Try again.');
  } catch (error) {
    if (at !== accountGeneration()) return false;
    useCollaborationStore.setState({ error: errorMessage(error) });
    stateFor(id, { error: errorMessage(error) });
    return false;
  } finally {
    lifecycle.delete(id);
    if (useCollaborationStore.getState().sharedMeta[id]) {
      const local = useProjectStore.getState().projects.find(p => p.id === id);
      if (local && !local.sharedMirror) useProjectStore.setState(state => ({ projects: state.projects.map(project => project.id === id ? { ...project, sharedMirror: { revision: useCollaborationStore.getState().sharedMeta[id].revision ?? 0 } } : project) }));
    }
  }
}

/* ⚠️⚠️ Forgotten on sign-out (lib/signOutReset): an edit held for the account that left (its
   Retry / Keep mine, shown to whoever opens the project next) and the edits queued behind one
   in flight — which would otherwise run, as the next account, once it finished. */
onSignOut(() => {
  useSharedProjectWrites.setState({ byProject: {} });
  for (const list of queued.values()) for (const item of list) item.done();
  queued.clear();
});
